/**
 * The editor session: what the endpoint editor currently holds (the unsaved
 * state of the Monaco models and the sidebar form), exposed to the copilot.
 *
 * The edit and new-endpoint routes register a session while mounted; the
 * copilot reads it to put an `[endpoint]` block into every turn's context
 * (so the code writer edits what the user sees, and a recorded turn replays
 * without any tool) and to apply a draft through `Endpoint.setDraft`.
 *
 * Framework-free registry, same shape as world.js: one current session,
 * subscribers notified on register / unregister.
 */

export const DRAFT_FIELDS = [
  'displayName',
  'name',
  'method',
  'script',
  'schemas',
  'originWhitelist',
  'enabled',
  'internal',
  'responseType',
  'isLongRunning',
  'allowPlatformWrites',
];

export const SCHEMA_KEYS = [
  'query',
  'body',
  'cookies',
  'headers',
  'successResponse',
  'errorResponse',
];

// A script above this size is summarised in the context block instead of
// inlined; the copilot then reads it through getDefinition / the MCP.
export const CONTEXT_SCRIPT_LIMIT = 24 * 1024;

// Delimiters of the inlined script in the [endpoint] block. Never anything
// that reads as an HTML tag: the request is inspected by a WAF.
export const SCRIPT_START = '===== script start =====';
export const SCRIPT_END = '===== script end =====';

let current = null;
const listeners = new Set();

function notify() {
  listeners.forEach(listener => listener(current));
}

/**
 * session: { id, mode: 'edit' | 'new', version: 'v1' | 'v2', endpointId,
 *            getDefinition(), applyDraft(patch), isDirty() }
 */
export function registerEditorSession(session) {
  if (!session || typeof session.getDefinition !== 'function') {
    throw new Error('An editor session needs getDefinition()');
  }
  current = session;
  notify();
  return () => unregisterEditorSession(session.id);
}

export function unregisterEditorSession(id) {
  if (current && current.id === id) {
    current = null;
    notify();
  }
}

export function getEditorSession() {
  return current;
}

export function subscribeEditorSession(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

/** Only the fields a draft may carry, with `schemas` narrowed to known keys. */
export function pickDraftFields(patch) {
  const draft = {};
  DRAFT_FIELDS.forEach(field => {
    if (patch[field] === undefined) {
      return;
    }
    if (field === 'schemas') {
      const schemas = {};
      SCHEMA_KEYS.forEach(key => {
        if (typeof patch.schemas[key] === 'string') {
          schemas[key] = patch.schemas[key];
        }
      });
      if (Object.keys(schemas).length) {
        draft.schemas = schemas;
      }
      return;
    }
    draft[field] = patch[field];
  });
  return draft;
}

function countLines(text) {
  return typeof text === 'string' && text.length ? text.split('\n').length : 0;
}

/** A short, human-readable account of what a draft changed. */
export function describeDraft(before, draft) {
  const changes = [];
  Object.keys(draft).forEach(field => {
    if (field === 'script') {
      changes.push(`script: ${countLines(before.script)} → ${countLines(draft.script)} lines`);
      return;
    }
    if (field === 'schemas') {
      Object.keys(draft.schemas).forEach(key => {
        if ((before.schemas || {})[key] !== draft.schemas[key]) {
          changes.push(`schemas.${key} updated`);
        }
      });
      return;
    }
    if (JSON.stringify(before[field]) !== JSON.stringify(draft[field])) {
      changes.push(`${field}: ${JSON.stringify(before[field])} → ${JSON.stringify(draft[field])}`);
    }
  });
  return changes;
}

/**
 * The `[endpoint]` block of the copilot's context: the editor's live state.
 * Returns '' when no editor is open.
 */
export function buildEndpointContextBlock(session = current) {
  if (!session) {
    return '';
  }
  let definition;
  try {
    definition = session.getDefinition() || {};
  } catch {
    return '';
  }
  const lines = [
    '[endpoint]',
    `mode: ${session.mode}`,
    `version: ${session.version}`,
    `id: ${session.mode === 'new' ? 'new' : session.endpointId}`,
    `dirty: ${typeof session.isDirty === 'function' ? session.isDirty() : false}`,
    `name: ${definition.name || ''}`,
    `displayName: ${definition.displayName || ''}`,
    `method: ${definition.method || ''}`,
    `enabled: ${definition.enabled === true}`,
    `internal: ${definition.internal === true}`,
    `responseType: ${definition.responseType || ''}`,
    `isLongRunning: ${definition.isLongRunning === true}`,
    `allowPlatformWrites: ${definition.allowPlatformWrites === true}`,
    `originWhitelist: ${JSON.stringify(definition.originWhitelist || [])}`,
  ];
  const schemas = definition.schemas || {};
  SCHEMA_KEYS.forEach(key => {
    if (typeof schemas[key] === 'string' && schemas[key].trim()) {
      lines.push(`schemas.${key}: ${schemas[key].replace(/\s+/g, ' ').trim()}`);
    }
  });
  const script = typeof definition.script === 'string' ? definition.script : '';
  if (script.length > CONTEXT_SCRIPT_LIMIT) {
    lines.push(
      `script: <${script.length} characters, ${countLines(script)} lines — too long to inline; read it with the Endpoint object's getDefinition tool>`
    );
  } else {
    // Plain delimiters on purpose: the run request crosses the platform's WAF,
    // whose XSS rules match anything that looks like an HTML tag (`<SCRIPT`).
    lines.push('script:');
    lines.push(SCRIPT_START);
    lines.push(script);
    lines.push(SCRIPT_END);
  }
  lines.push('[/endpoint]');
  return lines.join('\n');
}
